// src/pages/admin/PatientPreviousReports.jsx
import { useEffect, useMemo, useState } from "react";
import {
  Card,
  DataPageCard,
  PaginatedTable,
  MonthField,
  Button,
  FormField,
  EditPatientModal,
  ConfirmModal,
} from "@common";
import { usePatientsWithFilters, useDeletePatient } from "@api/hooks";
import { useAuth } from "@api/useAuth";
import { torontoMonthKey, canWriteClinicData } from "@utils";
import { getApiErrorMessage } from "@utils/apiErrorUtils";
import { toast } from "react-hot-toast";

export default function PatientPreviousReports({ clinicId }) {
  const { user } = useAuth();
  // Mirrors ClinicBasedCRUDWithDeletePermission on /api/patients/{id}/: Organizer,
  // Admin, Area Manager and Practitioner Lead can delete (the last two only within
  // their own clinics, enforced server-side). Observer/Bookkeeper are read-only.
  const canDelete = canWriteClinicData(user);
  const [month, setMonth] = useState(() => torontoMonthKey());
  const [searchName, setSearchName] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  // Modal states
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [patientToDelete, setPatientToDelete] = useState(null);

  const deletePatientMutation = useDeletePatient();

  // Debounce search input by 1 second
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchName.trim().toLowerCase());
    }, 500);
    return () => clearTimeout(timer);
  }, [searchName]);

  const filters = {
    clinic: clinicId,
    ...(debouncedSearch ? { search: debouncedSearch } : { date: month }),
    limit: pageSize,
    offset: (page - 1) * pageSize,
  };

  const {
    data: patientsData,
    isLoading,
    error,
  } = usePatientsWithFilters(filters, {
    enablePolling: true,
  });

  const handleEditClick = (patient) => {
    setSelectedPatient(patient);
    setEditModalOpen(true);
  };

  const handleEditClose = () => {
    setEditModalOpen(false);
    setSelectedPatient(null);
  };

  const handleDeleteClick = (patient) => {
    setPatientToDelete(patient);
    setDeleteModalOpen(true);
  };

  const handleDeleteClose = () => {
    if (deletePatientMutation.isPending) return;
    setDeleteModalOpen(false);
    setPatientToDelete(null);
  };

  const handleDeleteConfirm = async () => {
    if (!patientToDelete) return;
    try {
      await deletePatientMutation.mutateAsync(patientToDelete.id);
      toast.success("Patient deleted successfully!");
      setDeleteModalOpen(false);
      setPatientToDelete(null);
      // Deleting the last row of a page would otherwise leave an empty page.
      setPage((prev) => (data.length === 1 && prev > 1 ? prev - 1 : prev));
    } catch (error) {
      console.error("Failed to delete patient:", error);
      toast.error(getApiErrorMessage(error));
    }
  };

  const data = useMemo(() => {
    const results = patientsData?.results || [];
    return results.map((patient) => ({
      id: patient.id,
      date: patient.date || "-",
      name: patient.name || "-",
      leadCategory: patient.lead || "-",
      description: patient.description || "-",
      clinic: patient.clinic || "-",
    }));
  }, [patientsData]);

  const total = patientsData?.count || 0;

  const columns = [
    { key: "date", header: "DATE", nowrap: true },
    { key: "name", header: "NAME" },
    { key: "leadCategory", header: "LEAD CATEGORY" },
    { key: "description", header: "LEAD DESCRIPTION" },
    {
      key: "actions",
      header: "ACTIONS",
      nowrap: true,
      render: (row) => (
        <div className="flex gap-2">
          <Button
            size="sm"
            variant="outline"
            className="rounded-full border-2 border-palette-indigo-hover text-palette-indigo-heading hover:border-palette-indigo-ring"
            onClick={() => handleEditClick(row)}
          >
            Edit
          </Button>
          {canDelete && (
            <Button
              size="sm"
              variant="outline"
              className="rounded-full border-2 border-palette-red-badge text-palette-red-heading hover:bg-palette-red-soft hover:border-palette-red-heading"
              onClick={() => handleDeleteClick(row)}
            >
              Delete
            </Button>
          )}
        </div>
      ),
    },
  ];

  return (
    <Card className="mt-4">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-bold">Previous Reports</h3>
        <div className="flex items-center gap-3">
          <div className="w-[220px]">
            <FormField
              label="Search by Name"
              placeholder="Patient name..."
              value={searchName}
              onChange={(e) => {
                setSearchName(e.target.value);
                setPage(1);
              }}
            />
          </div>
          {!searchName.trim() && (
            <div className="w-[220px]">
              <MonthField
                label="Month"
                value={month}
                onChange={(newMonth) => {
                  setMonth(newMonth);
                  setPage(1);
                }}
              />
            </div>
          )}
        </div>
      </div>

      <DataPageCard
        isLoading={isLoading}
        error={error}
        isEmpty={data.length === 0}
        loadingMessage="Loading patients..."
        emptyMessage={`No patients found for ${clinicId} in ${month}`}
        showLoadingSpinner={true}
      >
        <PaginatedTable
          mode="server"
          columns={columns}
          data={data}
          rowKey={(r) => r.id}
          page={page}
          pageSize={pageSize}
          total={total}
          onPageChange={setPage}
          onPageSizeChange={(n) => {
            setPage(1);
            setPageSize(n);
          }}
        />
      </DataPageCard>

      <EditPatientModal
        isOpen={editModalOpen}
        onClose={handleEditClose}
        patient={selectedPatient}
      />

      <ConfirmModal
        isOpen={deleteModalOpen}
        onClose={handleDeleteClose}
        onConfirm={handleDeleteConfirm}
        title="Delete Patient"
        confirmText="Delete"
        confirmVariant="danger"
        isLoading={deletePatientMutation.isPending}
        message={
          <>
            <p>
              Are you sure you want to delete{" "}
              <span className="font-semibold text-palette-gray-heading">
                {patientToDelete?.name || "this patient"}
              </span>
              {patientToDelete?.date && patientToDelete.date !== "-"
                ? ` (${patientToDelete.date})`
                : ""}
              ?
            </p>
            <p className="mt-2">This action cannot be undone.</p>
          </>
        }
      />
    </Card>
  );
}
